import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IAd } from '../../interfaces/tasks';
import { usePublishedAdStatusColors } from '../../hooks/usePublishedAdStatusColors';
import { formatNumberWithComma } from '../../utils/number';
import { Button } from '@mui/material';
import AddScreenShotModal from './AddScreenShotModal';
import axios from 'axios';
import IResponse, { IPagination } from '../../interfaces/response';
import { useDebounce } from 'use-debounce';

export default function PublishedAdsTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [openScreenShotModal, setOpenScreenShotModal] = useState<boolean>(false);
	const [screenShotTitle, setScreenShotTitle] = useState('');
	const [screenShotPageId, setScreenShotPageId] = useState('');
	const [screenShotDescription, setScreenShotDescription] = useState('');
	const [files, setFiles] = useState<(File | string)[]>([]);
	const [loading, setLoading] = useState(false);
	const [savingScreenShot, setSavingScreenShot] = useState(false);
	const [selectedId, setSelectedId] = useState<string>('');
	const [fetchedData, setfetchedData] = useState<IAd[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewAd = (adId: string) => () => {
		navigate(`/campaigns/view/${adId}`);
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenScreenShotModal = (id: string) => () => {
		setSelectedId(id);
		setOpenScreenShotModal(true);
	};

	const handleCloseScreenShotModal = () => {
		setSelectedId('');
		setOpenScreenShotModal(false);
	};

	const handleCancelScreenShot = () => {
		handleCloseScreenShotModal();
	};

	const fetchPublishedAds = () => {
		setLoading(true);
		axios<IResponse<IAd[]>>({
			url: '/duties',
			params: {
				pending: 0,
				page: page,
				limit: pageSize,
				search: search,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setfetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSaveScreeShot = () => {
		const data = new FormData();
		data.append('image', files[0]);
		data.append('title', screenShotTitle);
		data.append('handle_name', screenShotPageId);
		data.append('description', screenShotDescription);

		setSavingScreenShot(true);

		axios<IResponse<{ data: boolean }>>({
			url: `/duties/screenshot/${selectedId}`,
			method: 'POST',
			data,
		})
			.then(res => {
				if (res.data.success) {
					fetchPublishedAds();
				}
			})
			.finally(() => {
				setSavingScreenShot(false);
				handleCloseScreenShotModal();
			});
	};

	const handleScreenShotTitleChange = (e: any) => {
		setScreenShotTitle(e.target.value);
	};

	const handleScreenShotPageIdChange = (e: any) => {
		setScreenShotPageId(e.target.value);
	};

	const handleScreenShotDescriptionChange = (e: any) => {
		setScreenShotDescription(e.target.value);
	};

	const properties: (keyof IAd)[] = ['category_title', 'content_type', 'media', 'confirmed'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<IAd>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'category_title',
			headerName: t('category'),
			width: 140,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'price',
			headerName: t('price_tooman'),
			width: 110,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				const formatted = formatNumberWithComma(params.value);
				return <div>{formatted}</div>;
			},
		},
		{
			field: 'content_type',
			headerName: t('content_type'),
			width: 100,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return t(params.value);
			},
		},
		{
			field: 'media',
			headerName: t('platform'),
			width: 90,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return t(params.value);
			},
		},
		{
			field: 'date',
			headerName: t('create_date'),
			width: 100,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'screenshot_status',
			headerName: t('screenshot'),
			width: 160,
			display: 'flex',
			headerClassName: `${headerClassNames} ps-11`,
			headerAlign: 'left',
			align: 'center',
			renderCell: params => {
				const buttonText = params.value ? t('added') : t('add');
				return (
					<Button size="large" className={`w-20 text-xs font-normal`} variant={'contained'} onClick={handleOpenScreenShotModal(params.row.id)} disabled={!params?.row?.confirmed || params.value}>
						{buttonText}
					</Button>
				);
			},
		},
		{
			field: 'confirmed',
			headerName: t('status'),
			width: 150,
			display: 'flex',
			headerClassName: `${headerClassNames} ps-12`,
			headerAlign: 'left',
			renderCell: params => {
				const { colors, label } = usePublishedAdStatusColors(params.value);
				return <div className={`ms-10 flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${colors}`}>{label}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 140,
			headerAlign: 'center',
			headerClassName: headerClassNames,
			align: 'center',
			renderCell: params => {
				return (
					<>
						<IconButton aria-label="eye" onClick={handleViewAd(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		fetchPublishedAds();
	}, [page, pageSize]);

	useEffect(() => {
		if (page === 1) {
			fetchPublishedAds();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<div className={'w-full'}>
			<BaranDataGrid<IAd> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} searchableFields={properties} />

			{/* Add Screenshot Modal */}
			<AddScreenShotModal loading={savingScreenShot} files={files} setFiles={setFiles} screenShotDescription={screenShotDescription} screenShotPageId={screenShotPageId} screenShotTitle={screenShotTitle} open={openScreenShotModal} onScreenShotPageIdChange={handleScreenShotPageIdChange} onScreenShotTitleChange={handleScreenShotTitleChange} onScreenShotDescriptionChange={handleScreenShotDescriptionChange} onClose={handleCloseScreenShotModal} onCancel={handleCancelScreenShot} onSave={handleSaveScreeShot} />
		</div>
	);
}
